import { useMemo, useRef } from 'react'

import { errorView, useWindow } from '@krp/client-runtime'
import { PrintSettingsScreen, aqsiPrintSettings, aqsiUpdatePrintSettings, newCommandIntent } from '@krp/resource-aqsi-ui'
import { StatusMessage } from '@krp/ui-kit'

import { useAppSelector } from '../../app/hooks'
import { useMySession } from './useMySession'

/** Настройка принадлежит кассе в адресе окна, а не последней выбранной кассе браузера. */
export function AqsiPrintSettingsBinding() {
  const window = useWindow()
  const resourceId = window.params.rid ?? ''
  const deviceId = Number(window.params.deviceId)
  return <RegisterPrintSettings key={`${resourceId}:${deviceId}`} resourceId={resourceId} deviceId={deviceId} />
}

function RegisterPrintSettings({ resourceId, deviceId }: { resourceId: string; deviceId: number }) {
  const valid = resourceId !== '' && Number.isSafeInteger(deviceId) && deviceId > 0
  const args = useMemo(() => ({ resourceId, deviceId }), [deviceId, resourceId])
  const subscription = aqsiPrintSettings.useQuerySubscription(args, { skip: !valid, refetchOnMountOrArgChange: true })
  const select = useMemo(() => aqsiPrintSettings.select(args), [args])
  const result = useAppSelector(select)
  const { session } = useMySession(resourceId)
  const [save, mutation] = aqsiUpdatePrintSettings.useMutation()
  const sending = useRef(false)
  if (!valid) return <StatusMessage kind="error" title="Нужна касса в адресе настройки печати" />
  const rawError = mutation.error ?? result.error
  const error = rawError === undefined ? null : errorView(rawError)
  const data = result.isError ? undefined : result.data
  return <PrintSettingsScreen
    key={`${resourceId}:${deviceId}:${data?.version ?? 'loading'}`}
    deviceId={deviceId}
    count={data?.printSlipCount ?? null}
    acceptQr={data?.acceptQr ?? null}
    fastPayment={data?.fastPayment ?? null}
    busy={mutation.isLoading || result.isLoading}
    canSave={session?.deviceId === deviceId}
    error={error === null ? null : { message: error.message, meta: `Код: ${error.code} · запрос ${error.correlationId ?? '—'}` }}
    saved={mutation.isSuccess}
    onReload={() => { mutation.reset(); void subscription.refetch() }}
    onSave={(count, acceptQr, fastPayment) => {
      if (sending.current || data === undefined || session?.deviceId !== deviceId) return
      sending.current = true
      void save({ ...args, count, acceptQr: acceptQr ?? data.acceptQr, fastPayment, expectedVersion: data.version, intent: newCommandIntent() })
        .finally(() => { sending.current = false })
    }}
  />
}
